import { Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Sparkles, Repeat, Music, Drama, ShoppingBasket, Palette, Dumbbell, Image, PartyPopper, Hammer } from "lucide-react";

const CAT_VIGNETTE = {
  concert: { grad: "from-fuchsia-500 to-purple-600", Icon: Music },
  spectacle: { grad: "from-indigo-500 to-violet-600", Icon: Drama },
  marche: { grad: "from-emerald-500 to-teal-600", Icon: ShoppingBasket },
  animation: { grad: "from-orange-400 to-rose-500", Icon: Palette },
  sport: { grad: "from-blue-500 to-cyan-600", Icon: Dumbbell },
  expo: { grad: "from-amber-400 to-orange-500", Icon: Image },
  fete: { grad: "from-rose-500 to-pink-600", Icon: PartyPopper },
  atelier: { grad: "from-teal-500 to-emerald-600", Icon: Hammer },
  autre: { grad: "from-slate-500 to-slate-700", Icon: Calendar },
};

const CAT_COLORS = {
  concert: "bg-fuchsia-100 text-fuchsia-800",
  spectacle: "bg-indigo-100 text-indigo-800",
  marche: "bg-emerald-100 text-emerald-800",
  animation: "bg-orange-100 text-orange-800",
  sport: "bg-blue-100 text-blue-800",
  expo: "bg-amber-100 text-amber-800",
  fete: "bg-rose-100 text-rose-800",
  atelier: "bg-teal-100 text-teal-800",
  autre: "bg-slate-100 text-slate-700",
};

export function formatDateRange(startISO, endISO) {
  if (!startISO) return "";
  const s = new Date(startISO);
  const e = endISO ? new Date(endISO) : null;
  const opts = { weekday: "short", day: "numeric", month: "short" };
  const timeOpts = { hour: "2-digit", minute: "2-digit" };
  const sDate = s.toLocaleDateString("fr-FR", opts);
  const sTime = s.toLocaleTimeString("fr-FR", timeOpts);
  if (!e) return `${sDate} · ${sTime}`;
  const sameDay = s.toDateString() === e.toDateString();
  if (sameDay) return `${sDate} · ${sTime} → ${e.toLocaleTimeString("fr-FR", timeOpts)}`;
  return `${sDate} → ${e.toLocaleDateString("fr-FR", opts)}`;
}

export default function EventCard({ event, recurrenceLabel, compact = false }) {
  const link = `/sortir/${event.slug || event.id}`;
  const isSoon = (() => {
    if (!event.start_date) return false;
    const diff = new Date(event.start_date).getTime() - Date.now();
    return diff > 0 && diff < 72 * 3600 * 1000;
  })();
  return (
    <Link to={link} data-testid={`event-card-${event.id}`}
          className="card-hover group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
      <div className={`relative ${compact ? "aspect-[16/10]" : "aspect-[4/3]"} bg-slate-100`}>
        {event.image ? (
          <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy"/>
        ) : (() => {
          const vg = CAT_VIGNETTE[event.category] || CAT_VIGNETTE.autre;
          const VIcon = vg.Icon;
          return (
            <div className={`w-full h-full bg-gradient-to-br ${vg.grad} grid place-items-center relative overflow-hidden group-hover:scale-105 transition-transform duration-500`} data-testid={`event-card-vignette-${event.id}`}>
              <VIcon size={compact ? 38 : 52} strokeWidth={1.5} className="text-white/90 drop-shadow"/>
              <span className="absolute bottom-2 right-3 text-white/85 text-[11px] font-bold uppercase tracking-wider">{event.category_label || event.category}</span>
            </div>
          );
        })()}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${CAT_COLORS[event.category] || CAT_COLORS.autre}`}>
            {event.category_label || event.category}
          </span>
          {event.featured && (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-amber-500 text-white inline-flex items-center gap-1"><Sparkles size={12}/> Mis en avant</span>
          )}
          {isSoon && (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-rose-500 text-white">Imminent</span>
          )}
          {recurrenceLabel && (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-600 text-white inline-flex items-center gap-1" data-testid="event-recurrence-badge"><Repeat size={12}/> {recurrenceLabel}</span>
          )}
        </div>
      </div>
      <div className={`${compact ? "p-3" : "p-4"} flex-1 flex flex-col`}>
        <div className={`font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1 ${compact ? "text-[11px]" : "text-xs"}`}>
          <Clock size={11}/> {formatDateRange(event.start_date, event.end_date)}
        </div>
        <h3 className={`mt-1 font-heading font-semibold text-slate-900 leading-snug line-clamp-2 ${compact ? "text-sm" : ""}`}>{event.title}</h3>
        {!compact && event.description && <p className="mt-1 text-sm text-slate-500 line-clamp-2">{event.description}</p>}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
          <span className="inline-flex items-center gap-1"><MapPin size={12}/> {event.city}</span>
          {event.organizer && <span className="truncate">{event.organizer}</span>}
        </div>
        {event.source_label && event.source !== "user" && (
          <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400" data-testid={`event-card-source-${event.id}`}>
            {event.source_logo && (
              <img src={event.source_logo} alt={event.source_label}
                   className="w-3.5 h-3.5 rounded-sm object-contain"
                   onError={(e) => {
                     if (event.source_logo_fallback && e.currentTarget.src !== event.source_logo_fallback) e.currentTarget.src = event.source_logo_fallback;
                     else e.currentTarget.style.display = "none";
                   }} />
            )}
            via {event.source_label}
          </div>
        )}
      </div>
    </Link>
  );
}
